import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { openAuthenticatedFile } from '@/lib/api';
import {
  FileText, Upload, MessageSquare, CreditCard, Clock, CheckCircle,
  XCircle, Send, Paperclip, User, ChevronRight, AlertCircle,
  Loader2, Eye, Download, RefreshCw, Calendar, DollarSign
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentPortalEnhanced = ({ user }) => {
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [payments, setPayments] = useState({ payments: [], total_paid: 0, fee_summary: null });
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Upload form
  const [uploading, setUploading] = useState(false);
  const [newDocument, setNewDocument] = useState({
    document_type: '',
    document_name: '',
    file: null,
    notes: ''
  });
  const fileInputRef = useRef(null);
  
  // Message form
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/student/documents`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/student/messages`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/student/payments`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/student/messages/unread-count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Initialize data on mount
  useEffect(() => {
    fetchDocuments();
    fetchMessages();
    fetchPayments();
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const uploadDocument = async () => {
    if (!newDocument.document_type || !newDocument.document_name || !newDocument.file) {
      toast.error('Please select a document type, name, and file');
      return;
    }
    if (newDocument.file.size > 25 * 1024 * 1024) {
      toast.error('File exceeds the 25MB upload limit');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('document_type', newDocument.document_type);
      formData.append('document_name', newDocument.document_name);
      if (newDocument.notes) formData.append('notes', newDocument.notes);

      const res = await fetch(`${API_URL}/api/student/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        toast.success('Document uploaded successfully');
        setNewDocument({ document_type: '', document_name: '', file: null, notes: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchDocuments();
      } else {
        let detail = 'Failed to upload document';
        try { const j = await res.json(); detail = j.detail || detail; } catch { /* ignore */ }
        toast.error(detail);
      }
    } catch (error) {
      toast.error('Error uploading document');
    }
    setUploading(false);
  };

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error('File exceeds the 25MB upload limit');
      return;
    }
    setNewDocument(prev => ({
      ...prev,
      file: f,
      document_name: prev.document_name || f.name.replace(/\.[^.]+$/, '')
    }));
  };

  const onFileDropped = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error('File exceeds the 25MB upload limit');
      return;
    }
    setNewDocument(prev => ({
      ...prev,
      file: f,
      document_name: prev.document_name || f.name.replace(/\.[^.]+$/, '')
    }));
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/student/messages`, {
        method: 'POST',
        headers,
        
        body: JSON.stringify({ content: newMessage })
      });
      
      if (res.ok) {
        setNewMessage('');
        fetchMessages();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
    setSendingMessage(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const documentTypes = [
    { value: 'passport', label: 'Passport' },
    { value: 'academic_transcript', label: 'Academic Transcript' },
    { value: 'english_test', label: 'English Test (IELTS/TOEFL)' },
    { value: 'financial_proof', label: 'Financial Proof' },
    { value: 'photo', label: 'Passport Photo' },
    { value: 'sop', label: 'Statement of Purpose' },
    { value: 'cv', label: 'CV/Resume' },
    { value: 'reference_letter', label: 'Reference Letter' },
    { value: 'other', label: 'Other' }
  ];

  const tabs = [
    { id: 'documents', label: 'My Documents', icon: FileText, count: documents.length },
    { id: 'messages', label: 'Messages', icon: MessageSquare, count: unreadCount },
    { id: 'payments', label: 'Payment History', icon: CreditCard }
  ];

  return (
    <div className="bg-white rounded-xl border shadow-sm" data-testid="student-portal">
      {/* Tab Navigation */}
      <div className="flex border-b overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-teal-500 text-teal-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`portal-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                tab.id === 'messages' && unreadCount > 0 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-teal-600" />
                Upload New Document
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                  <Select 
                    value={newDocument.document_type}
                    onValueChange={(v) => setNewDocument({...newDocument, document_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Name *</label>
                  <Input
                    value={newDocument.document_name}
                    onChange={(e) => setNewDocument({...newDocument, document_name: e.target.value})}
                    placeholder="e.g., John_Passport_2026"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={onFileDropped}
                    className="flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed border-teal-300 bg-teal-50/40 rounded-lg cursor-pointer hover:bg-teal-50 transition"
                    data-testid="student-doc-dropzone"
                  >
                    <Upload className="w-6 h-6 text-teal-600 mb-2" />
                    {newDocument.file ? (
                      <>
                        <p className="text-sm font-medium text-gray-800">{newDocument.file.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{(newDocument.file.size / (1024*1024)).toFixed(2)} MB · Click to replace</p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700">Drag &amp; drop a file here, or <span className="text-teal-600 font-medium">browse</span></p>
                        <p className="text-xs text-gray-500 mt-0.5">PDF, JPG, PNG, DOC, DOCX, XLS, XLSX up to 25MB</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={onFileSelected}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
                      className="hidden"
                      data-testid="student-doc-file-input"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <Input
                    value={newDocument.notes}
                    onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button 
                  onClick={uploadDocument} 
                  disabled={uploading}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Document
                </Button>
              </div>
            </div>

            {/* Documents List */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">My Documents</h3>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <FileText className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{doc.document_name}</p>
                          <p className="text-sm text-gray-500 capitalize">
                            {doc.document_type.replace('_', ' ')}
                          </p>
                          {doc.review_notes && (
                            <p className="text-xs text-gray-500 mt-1">
                              Review notes: {doc.review_notes}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(doc.status)}
                        <button 
                          onClick={async () => {
                            try {
                              // Prefer the authenticated download endpoint for new uploads
                              await openAuthenticatedFile(
                                `${API_URL}/api/student/documents/${doc.id}/download`,
                                doc.original_filename || doc.document_name
                              );
                            } catch (e) {
                              // Fallback for legacy URL-based records
                              if (doc.file_url && /^https?:/i.test(doc.file_url)) {
                                window.open(doc.file_url, '_blank');
                              } else {
                                toast.error('Failed to open document');
                              }
                            }
                          }}
                          className="text-teal-600 hover:text-teal-700 cursor-pointer"
                          data-testid={`view-doc-${doc.id}`}
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="flex flex-col h-[500px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Chat with Your Counselor</h3>
              <Button variant="outline" size="sm" onClick={fetchMessages}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto bg-gray-50 rounded-lg p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start a conversation with your counselor</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div 
                    key={msg.id}
                    className={`flex ${msg.sender_role === 'student' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${
                      msg.sender_role === 'student' 
                        ? 'bg-teal-500 text-white rounded-l-lg rounded-tr-lg' 
                        : 'bg-white border rounded-r-lg rounded-tl-lg'
                    } p-3 shadow-sm`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${
                          msg.sender_role === 'student' ? 'text-teal-100' : 'text-gray-500'
                        }`}>
                          {msg.sender_name}
                        </span>
                      </div>
                      <p className={msg.sender_role === 'student' ? 'text-white' : 'text-gray-800'}>
                        {msg.content}
                      </p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_role === 'student' ? 'text-teal-200' : 'text-gray-400'
                      }`}>
                        {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Message Input */}
            <div className="mt-4 flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1"
              />
              <Button 
                onClick={sendMessage} 
                disabled={sendingMessage || !newMessage.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                <p className="text-sm text-green-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-700">
                  £{payments.total_paid?.toLocaleString() || 0}
                </p>
              </div>
              {payments.fee_summary && (
                <>
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-600">Total Fee</p>
                    <p className="text-2xl font-bold text-blue-700">
                      £{payments.fee_summary.total_fee?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                    <p className="text-sm text-orange-600">Balance Due</p>
                    <p className="text-2xl font-bold text-orange-700">
                      £{payments.fee_summary.balance_due?.toLocaleString() || 0}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Payment History */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-4">Payment History</h3>
              {payments.payments?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No payment records yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.payments?.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          payment.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                        }`}>
                          <DollarSign className={`w-5 h-5 ${
                            payment.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{payment.description}</p>
                          <p className="text-sm text-gray-500 capitalize">
                            {payment.payment_type?.replace('_', ' ')} • {payment.payment_date || 'Pending'}
                          </p>
                          {payment.reference_number && (
                            <p className="text-xs text-gray-400">Ref: {payment.reference_number}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">
                          £{payment.amount?.toLocaleString()}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          payment.status === 'completed' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPortalEnhanced;
